import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_COMMAND_ERRORS, CHAT_COMMANDS, EVENT_CHAIN_STATUS } from 'cpool-constants';
import { deriveChatId } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useGetOwnCorporation } from '~/features/userStructures/hooks/corporation/useGetOwnCorporation';
import { findOwnDivisionPhoneNumberId } from '~/features/whatsapp/utils/findOwnDivisionPhoneNumberId';
import { invalidateInboxUnreadQueries } from '~/features/whatsapp/utils/invalidateInboxUnreadQueries';
import { toastChatCommandError } from '~/features/whatsapp/utils/toastChatCommandError';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

type StartPartnerChatInput = {
    partnerId: string;
    phoneE164: string;
};

// Frontend-only codes (no backend equivalent) — also their WHATSAPP_CHAT.ERROR translation keys.
export const DIVISION_LOAD_ERROR = 'DIVISION_LOAD_ERROR';
export const START_PARTNER_CHAT_NOT_PERMITTED = 'START_PARTNER_CHAT_NOT_PERMITTED';

export type StartPartnerChatDisabledReasonCode =
    | typeof CHAT_COMMAND_ERRORS.CALLING_USER_HAS_NO_DIVISION
    | typeof CHAT_COMMAND_ERRORS.DIVISION_HAS_NO_WHATSAPP_NUMBER
    | typeof DIVISION_LOAD_ERROR
    | typeof START_PARTNER_CHAT_NOT_PERMITTED;

export const useStartPartnerChatMutation = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const user = useUserInfo();
    const { permissions } = useAclPermissions();
    const { data: corporation, isError: isCorporationError } = useGetOwnCorporation();
    const invalidateOnChainComplete = useChainAwareInvalidation();

    const divisionId = user?.divisionId;
    const phoneNumberId = findOwnDivisionPhoneNumberId(corporation, divisionId);
    const isPermitted = permissions.commands[AGGREGATE_NAMES.CHAT][CHAT_COMMANDS.startPartnerChat];
    const canStart = Boolean(isPermitted && divisionId && phoneNumberId);
    const disabledReasonCode = ((): StartPartnerChatDisabledReasonCode | undefined => {
        if (canStart) return undefined;
        if (!isPermitted) return START_PARTNER_CHAT_NOT_PERMITTED;
        if (isCorporationError) return DIVISION_LOAD_ERROR;
        if (!divisionId) return CHAT_COMMAND_ERRORS.CALLING_USER_HAS_NO_DIVISION;
        return CHAT_COMMAND_ERRORS.DIVISION_HAS_NO_WHATSAPP_NUMBER;
    })();

    const mutation = useMutation({
        mutationFn: async ({ partnerId, phoneE164 }: StartPartnerChatInput) => {
            // Callers gate on `canStart`; this only guards against a stale click.
            if (!divisionId) {
                throw Object.assign(new Error('Calling user has no division'), {
                    response: CHAT_COMMAND_ERRORS.CALLING_USER_HAS_NO_DIVISION,
                });
            }
            if (!phoneNumberId) {
                throw Object.assign(new Error('Own division has no WhatsApp phone number'), {
                    response: CHAT_COMMAND_ERRORS.DIVISION_HAS_NO_WHATSAPP_NUMBER,
                });
            }
            const chatId = deriveChatId(phoneNumberId, phoneE164);
            const result = await APICommunicator.sendCommand(
                {
                    aggregateId: chatId,
                    aggregateName: AGGREGATE_NAMES.CHAT,
                    commandName: CHAT_COMMANDS.startPartnerChat,
                    payload: { divisionId, partnerId, phoneNumberId },
                },
                { allowNoop: true },
            );
            return { chatId, id: result?.id };
        },
        onError: (error) => {
            toastChatCommandError(error, t, 'WHATSAPP_CHAT.ERROR.START_CHAT_FAILED');
        },
        onSuccess: ({ id: correlationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
            if (user) {
                invalidateInboxUnreadQueries(queryClient, user.id);
            }

            // No correlationId → startPartnerChat no-op (already linked).
            if (!correlationId) return;

            invalidateOnChainComplete({
                correlationId,
                onComplete: (status) => {
                    if (status === EVENT_CHAIN_STATUS.COMPLETED) return;
                    toast.error(t('WHATSAPP_CHAT.ERROR.START_CHAT_CONFIRMATION_TIMEOUT'));
                },
                queryKeys: user
                    ? [queryKeys.getChats(), queryKeys.getInboxUnreadSummary(user.id)]
                    : [queryKeys.getChats()],
            });
        },
    });

    return { ...mutation, canStart, disabledReasonCode };
};
